import type { MetricData } from "@web-app/types";

import ChartWrapper from "./ChartWrapper";

interface MetricChartProps {
  data: MetricData;
  title: string;
}

export default function MetricChart({ title, data }: MetricChartProps) {
  const formatValue = (value: number, format: string) => {
    // Handle undefined or null values
    if (value === undefined || value === null) {
      return "0";
    }

    // Cap to 2 decimal places, dropping trailing zeros (e.g. 16.6666 -> 16.67)
    const rounded = Number(value.toFixed(2));

    switch (format) {
      case "currency":
        return `$${rounded.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
      case "percentage":
        return `${rounded}%`;
      case "score":
        return `${rounded}/10`;
      default:
        return rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
  };

  // If we have grouped metrics, render them
  if (data.metrics && data.metrics.length > 0) {
    const useVerticalLayout = data.metrics.length > 4;

    return (
      <ChartWrapper title={title}>
        {useVerticalLayout ? (
          <div className="overflow-y-auto h-full px-4 py-2">
            {data.metrics.map((metric, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0"
              >
                {data.metrics && data.metrics.length > 1 && (
                  <div className="text-sm text-gray-600 truncate mr-4">
                    {metric.label}
                  </div>
                )}
                <div className="text-lg font-bold text-gray-900 whitespace-nowrap">
                  {formatValue(metric.value, data.format)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-around h-full px-6 gap-6">
            {data.metrics.map((metric, index) => (
              <div key={index} className="flex-1 text-center py-4">
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {formatValue(metric.value, data.format)}
                </div>
                {data.metrics && data.metrics.length > 1 && (
                  <div className="text-sm text-gray-600">{metric.label}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </ChartWrapper>
    );
  }

  // Single metric rendering
  return (
    <ChartWrapper title={title}>
      <div className="flex flex-col justify-center h-full px-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {formatValue(data.value, data.format)}
          </div>
        </div>
      </div>
    </ChartWrapper>
  );
}
